import React, { useEffect, useState, FC } from 'react';
import { Psawidsimufin } from 'wid00-psabpf-simulation';
import { batch, useDispatch, useSelector } from 'react-redux';
import carDetailsDuck from 'src/redux/carDetails/carDetails.duck';
import { IPriceV2Types } from 'src/services';
import {
    FINANCE_SIMULATION_BASE_URL,
    FINANCE_SIMULATION_CLIENT_ID,
    isBrandOV,
    isMarketGB,
    isMarketIT,
} from 'src/constants/main';
import { IFinanceWidgetParameters } from 'src/redux/financeWidget/financeWidget.duck.interface';
import FinanceWidgetDuck from 'src/redux/financeWidget/financeWidget.duck';
import LoaderOverlay from '../LoaderOverlay';
import styled from 'styled-components';
import { IConfigurableCar, IDeal, IStockCar } from 'src/interfaces/Car';
import { captureException } from '@utils/sentry.utils';
import { getTotalPriceObjByType } from '@utils/Price.utils';
import { Redux } from 'src/redux/redux.interface';
import FinancePricesService from 'src/services/financePrices/financePrices.service';
import { IPriceV2 } from 'src/services/financePrices/financePrices.types';
import { HandledComponentError } from '@components/ErrorHandling';
import AssetManager from 'src/utils/externalDependencies/AssetManager';
import { JQueryDependency } from 'src/utils/externalDependencies/dependencies';
import { FinanceWidgetProps } from '@components/FinanceWidget/shared';
import { useFinanceWidgetLogic } from '@hooks/useFinanceWidgetLogic';

const StyledWidgetWrap = styled.div`
    .Fipsa-show-modal {
        z-index: 90000000;
    }
`;

const FinanceWidgetV1: FC<FinanceWidgetProps> = ({ registerRenderCb, generatedIndex }) => {
    try {
        const { getContext, handleWidgetOutput, isLoading } = useFinanceWidgetLogic(generatedIndex);

        const currentDeal = useSelector((state: Redux) => carDetailsDuck.getOwnState(state).currentDeal);
        const parameters = useSelector((state: Redux) => FinanceWidgetDuck.getFinanceWidgetParameters(state));
        const carDetails: IConfigurableCar & IStockCar = useSelector((state: Redux) =>
            carDetailsDuck.getCurrentCarDetails(state)
        );
        const financeQuote = useSelector((state: Redux) => FinanceWidgetDuck.getFinanceQuote(state));

        const [isLoadingPricesV2, setIsLoadingPricesV2] = useState(false);
        const [latestPricesV2, setLatestPricesV2] = useState<IPriceV2[]>([]);

        const { pricesV2, extraFields } = carDetails;

        const {
            breakdown: { deliveryInclTax, vehicleExciseDuty, registrationFee, versionBrandEStorePriceWithPromoCodeDealerDiscount },
        } = getTotalPriceObjByType(
            (isMarketIT && latestPricesV2.length && latestPricesV2) || extraFields?.pricesV2 || pricesV2,
            IPriceV2Types.TOTAL_B2C_FINANCE
        );
        const { finalPriceInclTax } =
            getTotalPriceObjByType(
                (isMarketIT && latestPricesV2.length && latestPricesV2) || extraFields?.pricesV2 || pricesV2,
                IPriceV2Types.B2C_FINANCE
            ) ?? {};

        const { finalPriceInclTax: basePrice, currency } =
            getTotalPriceObjByType(
                (isMarketIT && latestPricesV2.length && latestPricesV2) || extraFields?.pricesV2 || pricesV2,
                IPriceV2Types.CATALOG_FINANCE
            ) ?? {};

        const netPrice = isBrandOV && isMarketGB ? versionBrandEStorePriceWithPromoCodeDealerDiscount : finalPriceInclTax;

        const handleCallbackRegistration = (financeWidgetParameters: IFinanceWidgetParameters = parameters) => {
            registerRenderCb(() => {
                try {
                    Object.entries(financeWidgetParameters || {})?.forEach((financeWidgetParameter) =>
                        financeWidgetParameter[1] === null
                            ? delete (financeWidgetParameters as { [k: string]: any })[financeWidgetParameter[0]]
                            : 0
                    );

                    const Instance = Psawidsimufin.new({ selector: '#financial-widget' });
                    Instance.render({
                        context: getContext(
                            basePrice,
                            netPrice,
                            registrationFee,
                            vehicleExciseDuty,
                            deliveryInclTax,
                            financeWidgetParameters
                        ),
                        environment: {
                            WID_FINANCE_SIMU_BASE_URL: FINANCE_SIMULATION_BASE_URL,
                            WID_FINANCE_SIMU_CLIENT_ID: FINANCE_SIMULATION_CLIENT_ID,
                        },
                        outputFn: (output: any) => handleWidgetOutput(output, currency),
                    });
                } catch (e: any) {
                    console.error('Error when trying to init and render Finance Widget', e);
                    captureException(e);
                }
            });
        };

        useEffect(() => {
            // Let asset manager load JQuery as it is needed for Stellantis FW
            AssetManager.instance.addScripts([JQueryDependency]);
        }, []);

        useEffect(() => {
            const getLatestPricesV2 = (currentDeal: IDeal) => {
                setIsLoadingPricesV2(true);
                FinancePricesService.getLatestPrices(currentDeal)
                    .then((pricesV2) => {
                        setLatestPricesV2(pricesV2);
                        setIsLoadingPricesV2(false);
                    })
                    .catch((e) => {
                        captureException(e);
                        console.error(e);
                        setIsLoadingPricesV2(false);
                    });
                return;
            };

            if (isMarketIT && !isLoadingPricesV2) {
                getLatestPricesV2({ ...currentDeal, fullProductConfiguration: carDetails });
            }
            handleCallbackRegistration();
        }, [
            carDetails._id,
            carDetails.externalId,
            financeQuote,
            currentDeal,
            basePrice,
            netPrice,
            deliveryInclTax,
            vehicleExciseDuty,
            registrationFee,
        ]);

        return (
            <div>
                {isLoading && <LoaderOverlay loading={isLoading} />}
                <StyledWidgetWrap id="financial-widget" />
            </div>
        );
    } catch (e: any) {
        return <HandledComponentError error={e} />;
    }
};
FinanceWidgetV1.displayName = 'FinanceWidgetV1';

export default FinanceWidgetV1;
